import { useState } from "react";
import { useTheme } from "../context/ThemeContext";

// The whole lamp is one big button: click anywhere on it (bulb, cord, or knob)
// to toggle light/dark. It sways gently at rest and the knob gives a periodic
// tug to hint that it's pullable. The bulb glows warm in dark mode.
const LampToggle = () => {
  const { darkMode, toggleTheme } = useTheme();
  const [pulling, setPulling] = useState(false);

  const handlePull = () => {
    if (pulling) return;
    setPulling(true);
    // flip the theme partway through the pull for a snappy, tactile feel
    window.setTimeout(toggleTheme, 160);
    window.setTimeout(() => setPulling(false), 620);
  };

  const label = darkMode ? "Turn the lights off" : "Turn the lights on";

  return (
    <button
      type="button"
      onClick={handlePull}
      aria-label="Toggle light and dark mode"
      className={`group/lamp pointer-events-auto relative flex flex-col items-center origin-top px-3 -mx-3 pb-3 select-none focus:outline-none focus-visible:outline-none ${
        pulling ? "animate-lamp-swing" : "animate-lamp-idle"
      }`}
    >
      {/* hover / focus hint */}
      <span className="pointer-events-none absolute right-full top-9 mr-3 whitespace-nowrap rounded-lg bg-teal-500 px-2.5 py-1 text-xs font-medium text-white shadow-lg shadow-teal-500/30 opacity-0 translate-x-1 transition-all duration-300 group-hover/lamp:opacity-100 group-hover/lamp:translate-x-0 group-focus-visible/lamp:opacity-100 group-focus-visible/lamp:translate-x-0">
        {label}
        <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-teal-500" />
      </span>

      {/* cord from the toggle down to the bulb */}
      <div className="w-px h-9 md:h-11 bg-gradient-to-b from-gray-400/70 via-gray-400/50 to-gray-400/70 dark:from-gray-500/70 dark:via-gray-500/40 dark:to-gray-500/70" />

      {/* bulb */}
      <div className="relative">
        {/* warm halo, only in dark mode */}
        <div
          className={`absolute -inset-4 rounded-full blur-xl bg-amber-300/50 transition-opacity duration-500 ${
            darkMode ? "opacity-90" : "opacity-0"
          }`}
        />
        <div
          className={`relative w-7 h-7 md:w-8 md:h-8 rounded-full transition-all duration-500 ${
            darkMode
              ? "bg-gradient-to-b from-amber-100 to-amber-300 shadow-[0_0_26px_7px_rgba(252,211,77,0.5)]"
              : "bg-gradient-to-b from-gray-100 to-gray-300 dark:from-gray-600 dark:to-gray-700 shadow-sm group-hover/lamp:from-amber-50 group-hover/lamp:to-amber-200"
          }`}
        >
          {/* filament */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className={`h-2.5 w-1.5 rounded-b-full border-x-2 border-b-2 transition-colors duration-500 ${
                darkMode ? "border-amber-500/70" : "border-gray-400/50"
              }`}
            />
          </div>
        </div>
        {/* screw base */}
        <div className="mx-auto -mt-0.5 w-3 h-1.5 rounded-b bg-gray-400 dark:bg-gray-600" />
      </div>

      {/* pull string + knob — beckons at rest, snaps on pull */}
      <div
        className={`flex flex-col items-center ${
          pulling ? "animate-cord-pull" : "animate-knob-hint"
        }`}
      >
        <div className="w-px h-7 bg-gray-400/70 dark:bg-gray-500/70 transition-colors group-hover/lamp:bg-teal-400/80 group-focus-visible/lamp:bg-teal-400/80" />
        <div className="w-2.5 h-2.5 rounded-full bg-gray-400 dark:bg-gray-500 ring-4 ring-transparent transition-all group-hover/lamp:bg-teal-400 group-hover/lamp:ring-teal-400/25 group-hover/lamp:scale-125 group-focus-visible/lamp:bg-teal-400 group-focus-visible/lamp:ring-teal-400/25 group-focus-visible/lamp:scale-125" />
      </div>
    </button>
  );
};

export default LampToggle;
